/* eslint-disable react-refresh/only-export-components -- pure preview gate exported next to the modal that consumes the same props shape */
import throttle from 'lodash.throttle';
import {
  type ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate } from 'react-router';
import { Euler, type Object3D, Vector3 } from 'three';
import { useSnapshot } from 'valtio';

import { isMhrScene } from '@cartwhl/3d/gltf/utils';
import {
  type DeepPartial,
  Player,
  type PlayerCameraControlsHandle,
  type PlayerSettings,
  usePlayerSettings,
  usePlayerStore,
  usePlayerTimeline,
} from '@cartwhl/3d/player';
import type { BatchExportSettings } from '@cartwhl/common/api/getBatch';
import type { JobExportStatusResponse } from '@cartwhl/common/api/getJobExportStatus';
import { sanitizePromptAsFilename } from '@cartwhl/common/utils';
import { trackOutputExported } from '@cartwhl/common/utils/trackOutputExported';
import { trackOutputPreviewed } from '@cartwhl/common/utils/trackOutputPreviewed';
import Loading from '@cartwhl/ui/atoms/Loading';
import Slider from '@cartwhl/ui/atoms/Slider';
import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import ChevronLeftIcon from '@cartwhl/ui/icons/ChevronLeftIcon';
import DownloadIcon from '@cartwhl/ui/icons/DownloadIcon';
import EyeCrossedIcon from '@cartwhl/ui/icons/EyeCrossedIcon';
import EyeIcon from '@cartwhl/ui/icons/EyeIcon';
import PauseIcon from '@cartwhl/ui/icons/PauseIcon';
import PlayIcon from '@cartwhl/ui/icons/PlayIcon';
import Button from '@cartwhl/ui/input/Button';
import GoButton from '@cartwhl/ui/input/GoButton';
import Modal from '@cartwhl/ui/molecules/Modal';

import { noticesStore, userStore } from 'stores/global';

import {
  createMotionEditorSceneIdFromMotion,
  getMotionEditorSceneNameForJob,
  getMotionEditorScenePath,
} from '../MotionEditor/public';
import { batchBodyAccentBgClass } from './batchMotionBodyAccentClasses';
import {
  type MotionExportURLSlot,
  batchMotionPreviewHasMotionSource,
  cameraExportFilename,
  exportFileExtensionFromType,
  exportSlotIsReady,
  exportSlotsHaveReadyUrl,
  previewBvhUrlsFromExportSlots,
} from './batchMotionExportUrls';
import {
  mediaUrlPathKey,
  videoMimeTypeFromExtension,
} from './batchMotionPreviewPolling';
import { getBuiltinFreeCharacterMeshPath } from './freeCharacterMesh';
import { DEFAULT_MOTION_SCENE_CHARACTER_ROTATION } from './motionScene';

/** White + hairline border; pairs with `variant="secondary"` on large controls. */
const OUTLINE_WHITE_BUTTON =
  '!bg-white dark:!bg-gray-90 !border-gray-30 dark:!border-gray-75 border hover:!bg-gray-15 dark:hover:!bg-gray-75';

/** Minimal fields needed to decide if a batch job row can open the motion preview modal. */
export type BatchMotionPreviewableRow = {
  status?: string;
  bvhURL?: string;
  bvhURLs?: string[];
  mhrBvhURLs?: string[];
  mhrIdentityJsonURLs?: string[];
  exportURLs?: MotionExportURLSlot[];
};

export function batchMotionFileCanPreview(
  file: BatchMotionPreviewableRow,
): boolean {
  if (file.status !== 'COMPLETED') return false;
  if (batchMotionPreviewHasMotionSource(file)) return true;
  return exportSlotsHaveReadyUrl(file.exportURLs);
}

/** One row in the batch list for preview modal navigation. */
export type BatchPreviewNavigationJob = {
  id: string;
  name: string;
  /** 1-based position in the full batch list (matches thumbnail numbering). */
  batchMotionNumber: number;
  canPreview: boolean;
};

export function formatMotionModalTitle(
  batchMotionNumber: number,
  rawName: string,
  totalMotionsInBatch = 1,
): string {
  const base = rawName.trim() || 'motion';
  const withExt = base.toLowerCase().endsWith('.mp4') ? base : `${base}.mp4`;

  if (totalMotionsInBatch <= 1) {
    return withExt;
  }

  const padded = String(batchMotionNumber).padStart(2, '0');
  return `${padded}. ${withExt}`;
}

const DEFAULT_BATCH_PREVIEW_CHARACTER_ID = 'char-upload-Axel';
const MHR_BATCH_PREVIEW_CHARACTER_ID = 'char-upload-MHR';
const DEFAULT_BATCH_PREVIEW_MESH_PATH =
  getBuiltinFreeCharacterMeshPath(DEFAULT_BATCH_PREVIEW_CHARACTER_ID) ?? '';

/**
 * When the motion API returned aligned MHR studio assets, use the MHR free
 * character mesh and MHR BVHs for 3D preview. Export settings may target a
 * custom rig for downloads while the in-app preview always uses studio MHR.
 */
function resolveBatchPreviewCharacterId(
  exportSettings: BatchExportSettings | null,
  mhrBvhURLs: string[] | undefined,
  mhrIdentityJsonURLs: string[] | undefined,
): string {
  const fromExport = exportSettings?.characterID?.trim();
  const mhrAligned =
    !!mhrBvhURLs?.length &&
    !!mhrIdentityJsonURLs?.length &&
    mhrBvhURLs.length === mhrIdentityJsonURLs.length &&
    mhrBvhURLs.some((u) => u.trim()) &&
    mhrIdentityJsonURLs.some((u) => u.trim());

  if (mhrAligned) {
    return MHR_BATCH_PREVIEW_CHARACTER_ID;
  }
  if (fromExport) {
    return fromExport;
  }
  return DEFAULT_BATCH_PREVIEW_CHARACTER_ID;
}

/** Matches export “file type” column in batch UI (API stores kebab-case). */
function exportTypeDisplayLabel(exportType: string | undefined): string {
  const t = (exportType ?? '').toLowerCase().trim();
  const labels: Record<string, string> = {
    bvh: 'BVH',
    'fbx-maya': 'FBX (Maya)',
    'fbx-blender': 'FBX (Blender)',
    'fbx-unreal': 'FBX (Unreal)',
    'fbx-roblox': 'FBX (Roblox)',
    glb: 'GLB',
    ma: 'MA (Maya)',
    mb: 'MB (Maya)',
  };
  return labels[t] ?? (t ? t.toUpperCase() : 'Export');
}

const exportFileExtension = exportFileExtensionFromType;

export function formatExportSettingsSummary(
  settings: BatchExportSettings | null,
): string {
  if (!settings) {
    return '—';
  }
  const type = exportTypeDisplayLabel(settings.exportType);
  const fps = `${settings.frameRate} fps`;
  const meshOn = String(settings.includeMesh).toLowerCase() === 'true';
  const mesh = meshOn ? 'Mesh included' : 'No mesh';
  const exportType = (settings.exportType ?? '').toLowerCase().trim();
  const isMayaFormat = exportType === 'ma' || exportType === 'mb';
  if (isMayaFormat) {
    const ikParts = [
      String(settings.ikFeet).toLowerCase() === 'true' ? 'IK Feet' : null,
      String(settings.ikHands).toLowerCase() === 'true' ? 'IK Hands' : null,
    ].filter(Boolean);
    const ik = ikParts.length > 0 ? ikParts.join(', ') : 'No IK';
    return `${type} / ${fps} / ${mesh} / ${ik}`;
  }
  return `${type} / ${fps} / ${mesh}`;
}

function exportBaseNameFromJobTitle(jobTitle: string): string {
  const trimmed = jobTitle.trim() || 'motion';
  const noExt = trimmed.replace(/\.[^/.]+$/i, '');
  return sanitizePromptAsFilename(noExt, 120) || 'motion';
}

function bodyExportFilename(
  jobTitle: string,
  bodyIndex1: number,
  exportType: string | undefined,
  multiBody: boolean,
): string {
  const base = exportBaseNameFromJobTitle(jobTitle);
  const ext = exportFileExtension(exportType);
  if (multiBody) {
    const n = String(bodyIndex1).padStart(2, '0');
    return `${base}_${n}.${ext}`;
  }
  return `${base}.${ext}`;
}

const PREVIEW_ACTOR_ID = 'batch-comic-job-preview';

/**
 * Plays the source reference video in sync with the animation.
 *
 * Key design: when the animation is PLAYING we call `video.play()` and let
 * the browser decode naturally (same as the thumbnail tiles). Seeking on every
 * frame is what causes jitter — it forces the decoder to restart from the
 * nearest keyframe each time. We only seek when paused (scrubbing) or when
 * the animation loops back to the start.
 */
function BatchPreviewSourceVideoOverlay({
  videoUrl,
  videoFileExtension,
  syncActorId,
  frameRate,
}: {
  videoUrl: string;
  videoFileExtension?: string;
  syncActorId: string;
  frameRate: number;
}) {
  const { currentFrame, duration, isPlaying } = usePlayerTimeline(syncActorId);

  const [videoDuration, setVideoDuration] = useState<number | null>(null);
  const [canPlayThrough, setCanPlayThrough] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [aspectRatio, setAspectRatio] = useState(1);

  const videoRef = useRef<HTMLVideoElement>(null);
  /** Tracks the previous frame index to detect loops (large backward jumps). */
  const prevFrameRef = useRef(0);

  const videoPathKey = useMemo(() => mediaUrlPathKey(videoUrl), [videoUrl]);
  const videoMimeType = useMemo(
    () => videoMimeTypeFromExtension(videoFileExtension),
    [videoFileExtension],
  );

  /** Ratio of video duration to BVH duration. Compensates for minor length mismatches. */
  const durationQuotient =
    videoDuration && duration.seconds > 0
      ? videoDuration / duration.seconds
      : 1;

  /** Time in the video file corresponding to the current animation frame. */
  const videoTimeForFrame = useCallback(
    (frame: number) => (frame / frameRate) * durationQuotient,
    [frameRate, durationQuotient],
  );

  const throttledSeek = useMemo(
    () =>
      throttle((time: number) => {
        const v = videoRef.current;
        if (v) v.currentTime = time;
      }, 41.66),
    [],
  );

  useEffect(() => {
    return () => throttledSeek.cancel();
  }, [throttledSeek]);

  // Reset UI state when the underlying media object changes.
  useEffect(() => {
    setCanPlayThrough(false);
    setHasError(false);
    setVideoDuration(null);
    setAspectRatio(1);
    setExpanded(false);
    throttledSeek.cancel();
    prevFrameRef.current = 0;
  }, [videoPathKey, throttledSeek]);

  // Match thumbnail tiles: crossOrigin + assign src without remounting on query-only changes.
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !videoUrl) return;

    const cur = v.currentSrc || v.src || '';
    if (cur === videoUrl) return;

    const curPath = cur ? mediaUrlPathKey(cur) : null;
    if (curPath === videoPathKey && !v.error) return;

    v.src = videoUrl;
  }, [videoUrl, videoPathKey]);

  /**
   * Play / pause the video in lockstep with the animation.
   * When the animation plays, position the video at the current frame and let
   * `video.play()` handle smooth decode/buffering — exactly like the thumbnails.
   * When paused, pause and seek to the exact position.
   *
   * Deliberately does NOT include `currentFrame` so frame changes while playing
   * don't trigger seeks.
   */
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !canPlayThrough || !videoDuration || duration.seconds === 0)
      return;

    const time = videoTimeForFrame(currentFrame);
    if (isPlaying) {
      v.currentTime = time;
      v.playbackRate = durationQuotient;
      void v.play().catch(() => {});
    } else {
      v.pause();
      v.currentTime = time;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPlaying, canPlayThrough, videoDuration, duration.seconds]);

  /**
   * Handle frame changes:
   * - While paused: throttled seek for smooth scrubbing.
   * - While playing: detect loop (large backward jump) and re-sync the video.
   */
  useEffect(() => {
    const v = videoRef.current;
    if (!v || !canPlayThrough || !videoDuration || duration.seconds === 0)
      return;

    const prev = prevFrameRef.current;
    prevFrameRef.current = currentFrame;
    const delta = currentFrame - prev;

    if (!isPlaying) {
      const time = videoTimeForFrame(currentFrame);
      const rafId = requestAnimationFrame(() => throttledSeek(time));
      return () => cancelAnimationFrame(rafId);
    }

    // Animation looped back to start — re-sync video position.
    if (delta < -(frameRate * 0.5)) {
      const time = videoTimeForFrame(currentFrame);
      v.currentTime = time;
      void v.play().catch(() => {});
    }
  }, [
    currentFrame,
    isPlaying,
    canPlayThrough,
    videoDuration,
    duration.seconds,
    frameRate,
    videoTimeForFrame,
    throttledSeek,
  ]);

  if (hasError) return null;

  return (
    <div
      className={`pointer-events-none absolute top-3 right-3 z-16777272 flex justify-end rounded-2xl transition-all ease-linear ${
        expanded
          ? 'h-[220px] w-[220px] sm:h-[316px] sm:w-[316px] xl:h-[440px] xl:w-[440px] 2xl:h-[640px] 2xl:w-[640px]'
          : 'h-[90px] w-[90px] sm:h-[124px] sm:w-[124px] xl:h-[164px] xl:w-[164px] 2xl:h-[200px] 2xl:w-[200px]'
      }`}
    >
      <div
        className="group pointer-events-auto relative h-fit max-h-full w-fit max-w-full"
        style={{
          width: aspectRatio > 1 ? '100%' : `${aspectRatio * 100}%`,
          height: aspectRatio > 1 ? `${100 / aspectRatio}%` : '100%',
        }}
      >
        <Tooltip text="Expand video reference" side="left" disabled={expanded}>
          <button
            type="button"
            className="group-hover:bg-gray-90/20 absolute inset-0 z-10 h-full w-full rounded-2xl transition-colors"
            onClick={() => setExpanded((p) => !p)}
            aria-expanded={expanded}
            aria-label={
              expanded ? 'Shrink reference video' : 'Expand reference video'
            }
          />
        </Tooltip>
        <video
          key={videoPathKey}
          ref={videoRef}
          className="min-h-0 min-w-0 rounded-2xl transition-all"
          playsInline
          muted
          crossOrigin="anonymous"
          preload="auto"
          onLoadedMetadata={() => {
            const v = videoRef.current;
            if (!v) return;
            setVideoDuration(v.duration);
            setAspectRatio(
              v.videoWidth > 0 && v.videoHeight > 0
                ? v.videoWidth / v.videoHeight
                : 1,
            );
          }}
          onLoadedData={() => setCanPlayThrough(true)}
          onCanPlayThrough={() => setCanPlayThrough(true)}
          onError={() => setHasError(true)}
        >
          <source src={videoUrl} type={videoMimeType} />
        </video>
        {!canPlayThrough && (
          <div className="absolute top-0 right-0 flex h-2/3 w-full items-center justify-center rounded-2xl bg-white/20">
            <Loading className="text-white" />
          </div>
        )}
      </div>
    </div>
  );
}

function BatchMotionPreviewSyncedTransportBar({
  actorIds,
  showActorTagToggle,
}: {
  actorIds: string[];
  showActorTagToggle?: boolean;
}) {
  const store = usePlayerStore();
  const {
    settings: {
      scene: { showActorNameTags },
    },
    setSettings,
  } = usePlayerSettings();
  const primaryActorId = actorIds[0] ?? PREVIEW_ACTOR_ID;
  const { isPlaying, currentFrame, duration } =
    usePlayerTimeline(primaryActorId);
  const totalFrames = duration.frames;

  const playOrPause = useCallback(() => {
    if (actorIds.length === 0) return;
    if (store.state.transport.isPlaying) {
      store.pause();
    } else {
      store.play();
    }
  }, [actorIds.length, store]);

  const setFrameAll = useCallback(
    (frame: number) => {
      store.setFrame(frame);
    },
    [store],
  );

  return (
    <div className="pointer-events-auto flex min-w-0 items-center gap-2">
      <GoButton
        icon={isPlaying ? <PauseIcon /> : <PlayIcon />}
        onClick={() => playOrPause()}
      />
      <div className="text-gray-90 relative flex h-full items-center">
        <Text
          as="div"
          variant="studio/body/medium/sm"
          className="text-gray-90 text-end opacity-60"
          style={{
            width:
              totalFrames > 0 ? totalFrames.toString().length * 21 : undefined,
          }}
        >
          {!!totalFrames && (
            <>
              {currentFrame + 1}/{totalFrames}
            </>
          )}
        </Text>
      </div>
      <div className="pointer-events-auto flex h-10 min-w-0 flex-1 items-center px-2 lg:px-0">
        <Slider
          currentValue={totalFrames > 0 ? currentFrame + 1 : 1}
          min={1}
          max={Math.max(1, totalFrames)}
          step={1}
          size="sm"
          trackColor="player"
          rangeColor="player"
          disabled={totalFrames <= 1}
          onChange={(value) => setFrameAll(value - 1)}
        />
      </div>
      {showActorTagToggle ? (
        <Tooltip
          text={showActorNameTags ? 'Hide actor tags' : 'Show actor tags'}
          side="top"
          inModal
        >
          <Button
            type="button"
            variant="secondary"
            size="lg"
            className={`h-10 w-10 shrink-0 p-0 ${OUTLINE_WHITE_BUTTON}`}
            aria-pressed={showActorNameTags}
            aria-label={
              showActorNameTags ? 'Hide actor tags' : 'Show actor tags'
            }
            onClick={() => {
              setSettings({
                scene: {
                  showActorNameTags: !showActorNameTags,
                },
              });
            }}
            icon={
              showActorNameTags ? (
                <EyeCrossedIcon className="h-5 w-5" />
              ) : (
                <EyeIcon className="h-5 w-5" />
              )
            }
          />
        </Tooltip>
      ) : null}
    </div>
  );
}

export type BatchMotionJobPreviewModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  jobId: string;
  jobTitle: string;
  /** 1-based slot in the batch (for title and “n of total”). */
  batchMotionNumber: number;
  /** Total motions in the batch (denominator for “n of total”). */
  totalMotionsInBatch: number;
  /** User-requested export presigned URLs (downloads only). */
  exportURLs?: MotionExportURLSlot[];
  /** Download basenames aligned with `exportURLs` (from API). */
  exportFilenames?: (string | undefined)[];
  /** Studio / comic BVHs for 3D preview playback. */
  bvhURL?: string;
  bvhURLs?: string[];
  mhrBvhURLs?: string[];
  mhrIdentityJsonURLs?: string[];
  /** Presigned camera FBX URL when present on the motion record. */
  cameraFbxURL?: string;
  exportSettings: BatchExportSettings | null;
  /**
   * Called when the user clicks Download. Pass `{ bodyIndex }` for one export slot,
   * `{ camera: true }` for the camera FBX, or omit to download every ready file.
   */
  onDownload?: (opts?: {
    bodyIndex?: number;
    camera?: boolean;
  }) => void | Promise<void>;
  /**
   * Legacy zip path: polled when `exportURLs` are absent. Ignored when presigned
   * export URLs are already on the motion row.
   */
  pollJobZipExportReadiness?: () => Promise<JobExportStatusResponse>;
  /**
   * Full batch row list in order; prev/next only visit rows with `canPreview`.
   * Omit to hide navigation (single-job or non-batch context).
   */
  previewNavigation?: {
    jobs: BatchPreviewNavigationJob[];
    onSelectJobId: (jobId: string) => void;
  };
  /**
   * Reference clip (presigned MP4) shown top-right and scrubbed with the preview timeline,
   * matching the studio timeline video overlay. Batch comic passes studio / CWMS preview URL.
   */
  sourceVideoOverlayUrl?: string;
  /** From GET /media/{id} — used for `<source type>` when the upload is not MP4. */
  sourceVideoFileExtension?: string;
  /** Hides export/download UI (e.g. canvas project history previews). */
  hideDownloadUI?: boolean;
  /** Modal shell width in px; defaults to batch-page layout (960). */
  modalMaxWidth?: number;
  /** Extra controls in the title row (e.g. asset library bookmark). */
  titleTrailing?: ReactNode;
  /** Extra controls beside Close in the footer (e.g. move to trash). */
  footerLeadingExtra?: ReactNode;
  /**
   * AnalyticsApp value for Metrics events from this modal
   * (e.g. `comic`, `swing`, `library`).
   */
  metricsApp: string;
  /** Batch / process id for time-to-first-frame (`outputPreviewed`). */
  batchID?: string;
};

export default function BatchMotionJobPreviewModal({
  open,
  onOpenChange,
  jobId,
  jobTitle,
  batchMotionNumber,
  totalMotionsInBatch,
  exportURLs,
  exportFilenames,
  bvhURL = '',
  bvhURLs,
  mhrBvhURLs,
  mhrIdentityJsonURLs,
  cameraFbxURL,
  exportSettings,
  onDownload,
  pollJobZipExportReadiness,
  previewNavigation,
  sourceVideoOverlayUrl,
  sourceVideoFileExtension,
  hideDownloadUI = false,
  modalMaxWidth = 960,
  titleTrailing,
  footerLeadingExtra,
  metricsApp,
  batchID,
}: BatchMotionJobPreviewModalProps) {
  const [fitTarget, setFitTarget] = useState<Object3D | null>(null);
  const [batchPreviewRootRotation, setBatchPreviewRootRotation] =
    useState<Euler>(DEFAULT_MOTION_SCENE_CHARACTER_ROTATION);
  const cameraControlsRef = useRef<PlayerCameraControlsHandle | null>(null);
  const [openingEditor, setOpeningViewer] = useState(false);
  const navigate = useNavigate();
  const { apiKey: apiKeyState } = useSnapshot(userStore.state);
  const apiKey = apiKeyState.data?.apiKey ?? '';

  const characterID = useMemo(
    () =>
      resolveBatchPreviewCharacterId(
        exportSettings,
        mhrBvhURLs,
        mhrIdentityJsonURLs,
      ),
    [exportSettings, mhrBvhURLs, mhrIdentityJsonURLs],
  );
  const meshPath = useMemo(
    () =>
      getBuiltinFreeCharacterMeshPath(characterID) ??
      DEFAULT_BATCH_PREVIEW_MESH_PATH,
    [characterID],
  );

  const { animationUrls, animationIdentityPaths } = useMemo(() => {
    if (
      characterID === MHR_BATCH_PREVIEW_CHARACTER_ID &&
      mhrBvhURLs?.length &&
      mhrIdentityJsonURLs?.length === mhrBvhURLs.length
    ) {
      const urls: string[] = [];
      const identityPaths: (string | undefined)[] = [];
      for (let i = 0; i < mhrBvhURLs.length; i += 1) {
        const bvh = mhrBvhURLs[i]?.trim();
        if (!bvh) continue;
        urls.push(bvh);
        const id = mhrIdentityJsonURLs[i]?.trim();
        identityPaths.push(id || undefined);
      }
      if (urls.length > 0) {
        return {
          animationUrls: urls,
          animationIdentityPaths: identityPaths,
        };
      }
    }
    const extra = bvhURLs?.map((u) => u.trim()).filter(Boolean) ?? [];
    if (extra.length > 0) {
      return { animationUrls: extra, animationIdentityPaths: undefined };
    }
    if (bvhURL.trim()) {
      return { animationUrls: [bvhURL], animationIdentityPaths: undefined };
    }
    const fromExports = previewBvhUrlsFromExportSlots(exportURLs);
    if (fromExports.length > 0) {
      return { animationUrls: fromExports, animationIdentityPaths: undefined };
    }
    return { animationUrls: [], animationIdentityPaths: undefined };
  }, [
    bvhURL,
    bvhURLs,
    characterID,
    exportURLs,
    mhrBvhURLs,
    mhrIdentityJsonURLs,
  ]);

  const actorIds = useMemo(() => {
    const n = animationUrls.length;
    if (n <= 1) return [PREVIEW_ACTOR_ID];
    return Array.from({ length: n }, (_, i) => `${PREVIEW_ACTOR_ID}-${i}`);
  }, [animationUrls]);

  const sceneMotionCount = useMemo(
    () => animationUrls.filter((url) => url.trim()).length,
    [animationUrls],
  );

  const handleOpenInEditor = useCallback(async () => {
    if (!apiKey) {
      noticesStore.error('API key not available. Please try again.');
      return;
    }
    if (!jobId.trim() || sceneMotionCount === 0) {
      noticesStore.error('No completed motion is available to open.');
      return;
    }

    setOpeningViewer(true);
    try {
      const sceneID = await createMotionEditorSceneIdFromMotion({
        apiKey,
        motionID: jobId,
        outputCount: sceneMotionCount,
        sceneName: getMotionEditorSceneNameForJob(jobTitle, jobId),
        playbackOverrides: {
          frameRate: exportSettings?.frameRate,
          frameStepSize: exportSettings?.frameStepSize,
        },
      });

      noticesStore.success('Scene created');
      onOpenChange(false);
      navigate(getMotionEditorScenePath(sceneID));
    } catch (error) {
      noticesStore.error(
        error instanceof Error ? error.message : 'Failed to create scene.',
      );
    } finally {
      setOpeningViewer(false);
    }
  }, [
    apiKey,
    exportSettings?.frameRate,
    exportSettings?.frameStepSize,
    jobId,
    jobTitle,
    navigate,
    onOpenChange,
    sceneMotionCount,
  ]);

  useEffect(() => {
    if (!open) {
      setFitTarget(null);
    }
    setBatchPreviewRootRotation(DEFAULT_MOTION_SCENE_CHARACTER_ROTATION);
  }, [
    open,
    jobId,
    bvhURL,
    bvhURLs,
    exportURLs,
    mhrBvhURLs,
    mhrIdentityJsonURLs,
  ]);

  const handlePrimarySceneReady = useCallback(
    (scene: Object3D) => {
      setFitTarget(scene);
      setBatchPreviewRootRotation(
        isMhrScene(scene)
          ? new Euler(0, 0, 0)
          : DEFAULT_MOTION_SCENE_CHARACTER_ROTATION,
      );
      if (batchID) {
        trackOutputPreviewed({
          eventSource: 'applets',
          app: metricsApp,
          jobID: jobId,
          batchID,
          format: 'bvh',
        });
      }
    },
    [batchID, jobId, metricsApp],
  );

  const fitCharacterKey = useMemo(
    () => animationUrls.join('|'),
    [animationUrls],
  );

  /**
   * First open can race the auto `fitCharacter` settle (controls vs mesh bounds).
   * Explicit `frame()` matches the "Frame character" control and retries until it succeeds.
   */
  useEffect(() => {
    if (!open || !fitTarget) return undefined;

    let cancelled = false;
    let attempts = 0;
    const maxAttempts = 36;

    const tryFrame = () => {
      if (cancelled) return;
      const ctrl = cameraControlsRef.current;
      const ok = ctrl?.frame?.(true) ?? false;
      attempts += 1;
      if (!ok && attempts < maxAttempts) {
        requestAnimationFrame(tryFrame);
      }
    };

    const id = requestAnimationFrame(() => {
      requestAnimationFrame(tryFrame);
    });

    return () => {
      cancelled = true;
      cancelAnimationFrame(id);
    };
  }, [open, fitTarget, jobId, fitCharacterKey]);

  /** Per-body BVHs may already carry world / root layout for the scene (e.g. two people at a table). */
  const characterPositions = useMemo(
    () => animationUrls.map(() => new Vector3(0, 0, 0)),
    [animationUrls],
  );

  const showCharacterTaggingControls = animationUrls.length > 1;

  const initialSettings = useMemo<DeepPartial<PlayerSettings>>(
    () => ({
      playback: {
        frameRate: exportSettings?.frameRate ?? 30,
        frameStepSize: exportSettings?.frameStepSize ?? 1,
        autoplay: false,
        loop: true,
      },
      character: {
        showSkeleton: false,
      },
      environment: {
        showGrid: true,
        gridCellSize: 1,
      },
      scene: {
        showActorNameTags: showCharacterTaggingControls,
      },
    }),
    [
      exportSettings?.frameRate,
      exportSettings?.frameStepSize,
      showCharacterTaggingControls,
    ],
  );

  const playerRootId = useMemo(
    () =>
      `batch-comic-preview-${jobId.replace(/[^a-zA-Z0-9_-]/g, '-') || 'job'}`,
    [jobId],
  );

  const trimmedSourceVideoUrl = useMemo(
    () => sourceVideoOverlayUrl?.trim() ?? '',
    [sourceVideoOverlayUrl],
  );

  const [downloadBusy, setDownloadBusy] = useState<
    number | 'all' | 'camera' | null
  >(null);

  const useDirectExportDownloads = Boolean(exportURLs?.length);
  const needsExportPoll =
    Boolean(pollJobZipExportReadiness) && !useDirectExportDownloads;
  const [jobZipExportReady, setJobZipExportReady] = useState(!needsExportPoll);
  const [exportPollShowsFailure, setExportPollShowsFailure] = useState(false);

  useEffect(() => {
    if (!needsExportPoll) {
      setJobZipExportReady(true);
      setExportPollShowsFailure(false);
      return;
    }
    if (!open || !pollJobZipExportReadiness) {
      setJobZipExportReady(false);
      setExportPollShowsFailure(false);
      return;
    }

    let cancelled = false;

    const poll = async () => {
      try {
        const r = await pollJobZipExportReadiness();
        if (cancelled) return;
        if (r.downloadExportsReady) {
          setJobZipExportReady(true);
          setExportPollShowsFailure(false);
        } else {
          setJobZipExportReady(false);
          if (r.hasFailedExport) {
            setExportPollShowsFailure(true);
          }
        }
      } catch {
        if (!cancelled) {
          setJobZipExportReady(false);
        }
      }
    };

    setJobZipExportReady(false);
    setExportPollShowsFailure(false);
    void poll();
    const id = setInterval(() => void poll(), 5000);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [open, jobId, needsExportPoll, pollJobZipExportReadiness]);

  const zipDownloadBlocked =
    needsExportPoll && (!jobZipExportReady || exportPollShowsFailure);

  const exportSettingsSummary = useMemo(
    () => formatExportSettingsSummary(exportSettings),
    [exportSettings],
  );

  const downloadBodyCount = useMemo(() => {
    if (exportURLs?.length) {
      return exportURLs.length;
    }
    return Math.max(animationUrls.length, 1);
  }, [exportURLs, animationUrls.length]);

  const multiBodyExport = (exportURLs?.length ?? 0) > 1;

  const hasCameraDownload = Boolean(cameraFbxURL?.trim());
  const cameraFilename = hasCameraDownload
    ? cameraExportFilename(jobTitle, exportFilenames)
    : null;

  const bodyDownloadRows = useMemo(
    () =>
      Array.from({ length: downloadBodyCount }, (_, idx) => {
        const bodyIndex = idx + 1;
        const apiFilename = exportFilenames?.[idx];
        return {
          bodyIndex,
          label: `Actor ${bodyIndex}`,
          filename:
            apiFilename?.trim() ||
            bodyExportFilename(
              jobTitle,
              bodyIndex,
              exportSettings?.exportType,
              multiBodyExport,
            ),
          dotClass: batchBodyAccentBgClass(idx),
        };
      }),
    [
      downloadBodyCount,
      exportFilenames,
      jobTitle,
      exportSettings?.exportType,
      multiBodyExport,
    ],
  );

  const showRowZipWaiting =
    needsExportPoll && !exportPollShowsFailure && !jobZipExportReady;

  const zipDownloadEnabled =
    Boolean(onDownload) && downloadBusy === null && !zipDownloadBlocked;

  const allDownloadEnabled =
    Boolean(onDownload) &&
    downloadBusy === null &&
    !exportPollShowsFailure &&
    (useDirectExportDownloads
      ? exportSlotsHaveReadyUrl(exportURLs)
      : zipDownloadEnabled);

  const exportControlsAvailable = Boolean(onDownload);

  const isRowDownloadEnabled = useCallback(
    (bodyIndex: number) => {
      if (!onDownload || downloadBusy !== null || exportPollShowsFailure) {
        return false;
      }
      if (useDirectExportDownloads) {
        return exportSlotIsReady(exportURLs, bodyIndex);
      }
      return zipDownloadEnabled;
    },
    [
      onDownload,
      downloadBusy,
      exportPollShowsFailure,
      useDirectExportDownloads,
      exportURLs,
      zipDownloadEnabled,
    ],
  );

  const isCameraDownloadEnabled =
    Boolean(onDownload) &&
    downloadBusy === null &&
    !exportPollShowsFailure &&
    hasCameraDownload;

  const handleDownload = useCallback(
    async (scope?: { bodyIndex?: number; camera?: boolean }) => {
      if (!onDownload) return;
      const tag = scope?.camera
        ? ('camera' as const)
        : scope?.bodyIndex !== undefined
          ? scope.bodyIndex
          : ('all' as const);
      setDownloadBusy(tag);

      const fileCount = scope?.camera
        ? 1
        : scope?.bodyIndex !== undefined
          ? 1
          : downloadBodyCount + (hasCameraDownload ? 1 : 0);
      trackOutputExported({
        eventSource: 'applets',
        app: metricsApp,
        jobID: jobId,
        format: scope?.camera ? 'fbx-camera' : exportSettings?.exportType,
        fileCount,
      });

      try {
        await onDownload(
          scope?.camera
            ? { camera: true }
            : scope?.bodyIndex !== undefined
              ? { bodyIndex: scope.bodyIndex }
              : undefined,
        );
      } finally {
        setDownloadBusy(null);
      }
    },
    [
      onDownload,
      metricsApp,
      jobId,
      exportSettings?.exportType,
      downloadBodyCount,
      hasCameraDownload,
    ],
  );

  const previewableOrdered = useMemo(
    () => (previewNavigation?.jobs ?? []).filter((j) => j.canPreview),
    [previewNavigation?.jobs],
  );

  const goAdjacentPreview = useCallback(
    (delta: -1 | 1) => {
      if (!previewNavigation?.onSelectJobId || previewableOrdered.length <= 1) {
        return;
      }
      const i = previewableOrdered.findIndex((j) => j.id === jobId);
      if (i < 0) return;
      const len = previewableOrdered.length;
      const next = (i + delta + len) % len;
      previewNavigation.onSelectJobId(previewableOrdered[next]!.id);
    },
    [previewNavigation, previewableOrdered, jobId],
  );

  const modalTitle = (
    <div className="flex w-full min-w-0 items-center justify-between gap-3">
      <Text
        as="div"
        variant="studio/title/md"
        className="text-primary min-w-0 flex-1 truncate pb-1 text-start text-[20px]/[20px]"
      >
        {formatMotionModalTitle(
          batchMotionNumber,
          jobTitle,
          totalMotionsInBatch,
        )}
      </Text>
      {previewNavigation && totalMotionsInBatch > 1 ? (
        <div className="flex shrink-0 items-center gap-3">
          <Text
            as="span"
            variant="studio/body/md"
            className="text-gray-60 whitespace-nowrap"
          >
            {batchMotionNumber} of {totalMotionsInBatch}
          </Text>
          <Button
            type="button"
            variant="secondary"
            size="lg"
            className={`h-10 w-10 shrink-0 p-0 ${OUTLINE_WHITE_BUTTON}`}
            disabled={previewableOrdered.length <= 1}
            icon={<ChevronLeftIcon className="h-5 w-5" />}
            aria-label="Previous motion"
            onClick={() => goAdjacentPreview(-1)}
          />
          <Button
            type="button"
            variant="secondary"
            size="lg"
            className={`h-10 w-10 shrink-0 p-0 ${OUTLINE_WHITE_BUTTON}`}
            disabled={previewableOrdered.length <= 1}
            icon={
              <ChevronLeftIcon className="h-5 w-5 rotate-180" aria-hidden />
            }
            aria-label="Next motion"
            onClick={() => goAdjacentPreview(1)}
          />
        </div>
      ) : null}
      {titleTrailing}
    </div>
  );

  const modalFooter = (
    <div className="flex flex-wrap items-center justify-between gap-3 gap-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="secondary"
          size="lg"
          className={OUTLINE_WHITE_BUTTON}
          onClick={() => onOpenChange(false)}
        >
          Close
        </Button>
        {footerLeadingExtra}
      </div>
      <div className="flex flex-wrap items-center justify-end gap-2">
        <Button
          type="button"
          variant="secondary"
          size="lg"
          className={OUTLINE_WHITE_BUTTON}
          disabled={openingEditor}
          loading={openingEditor}
          onClick={handleOpenInEditor}
        >
          Open in Motion Editor
        </Button>
        {hideDownloadUI ? null : (
          <Button
            type="button"
            variant="primary"
            size="lg"
            disabled={!allDownloadEnabled}
            loading={downloadBusy === 'all'}
            onClick={() => void handleDownload()}
          >
            {downloadBusy === 'all'
              ? 'Downloading...'
              : needsExportPoll &&
                  exportControlsAvailable &&
                  !jobZipExportReady &&
                  !exportPollShowsFailure
                ? 'Preparing download...'
                : useDirectExportDownloads && (exportURLs?.length ?? 0) > 1
                  ? 'Download all files'
                  : 'Download all'}
          </Button>
        )}
      </div>
    </div>
  );

  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title={modalTitle}
      maxWidth={modalMaxWidth}
      fixedHeightContent={false}
      fullscreenOnMobile
      horizontalBleed
      verticalBleed
      titleClassName="!mb-3 !px-3 !pt-3 lg:!mb-4 lg:!px-4 lg:!pt-4"
      footer={modalFooter}
      footerClassName="border-gray-30 dark:border-gray-75 border-t px-3 pb-3 pt-3 lg:px-4 lg:pb-3 lg:pt-3"
    >
      <div className="flex flex-col gap-3 px-3 pt-0 pb-3 lg:gap-4 lg:px-4 lg:pb-4">
        <div
          className={`border-primary bg-secondary dark:bg-gray-90 relative aspect-video w-full shrink-0 overflow-hidden rounded-xl border${modalMaxWidth < 960 ? 'mx-auto max-w-2xl' : ''}`}
        >
          <Player.Root
            key={playerRootId}
            id={playerRootId}
            hotkeysEnabled={false}
            initialSettings={initialSettings}
          >
            <Player.Canvas className="absolute inset-0 h-full w-full rounded-none! bg-transparent!">
              <Player.Environment />
              <Player.DefaultCamera position={[0, 1, 10]} zoom={2} />
              <Player.CameraControls
                cameraControlsRef={cameraControlsRef}
                fitCharacter
                fitCharacterKey={fitCharacterKey}
                fitCharacterTarget={fitTarget}
                fitCharacterDistanceMultiplier={1.15}
                relockTargetOnPlaybackStop={false}
              />
              <group rotation={batchPreviewRootRotation}>
                {animationUrls.map((url, i) => (
                  <Player.Character
                    key={`${actorIds[i]}-${i}`}
                    actorId={actorIds[i]!}
                    characterID={characterID}
                    meshPath={meshPath}
                    meshFormat="glb"
                    animation={{ type: 'url', url }}
                    identityPath={animationIdentityPaths?.[i]}
                    position={characterPositions[i]!}
                    rotationOffsets={exportSettings?.rotationOffsets}
                    skinHex={exportSettings?.skinHex}
                    faceExpression={exportSettings?.faceExpression}
                    onSceneReady={i === 0 ? handlePrimarySceneReady : undefined}
                  />
                ))}
              </group>
            </Player.Canvas>
            <div className="pointer-events-none absolute right-3 bottom-3 left-3 z-10">
              <BatchMotionPreviewSyncedTransportBar
                actorIds={actorIds}
                showActorTagToggle={showCharacterTaggingControls}
              />
            </div>
            <div className="pointer-events-auto absolute top-3 left-3 z-20">
              <Player.CameraButtons
                cameraControlsRef={cameraControlsRef}
                inModal
                variant="simple"
                tooltipSide="right"
              />
            </div>
            {trimmedSourceVideoUrl ? (
              <BatchPreviewSourceVideoOverlay
                videoUrl={trimmedSourceVideoUrl}
                videoFileExtension={sourceVideoFileExtension}
                syncActorId={actorIds[0] ?? PREVIEW_ACTOR_ID}
                frameRate={exportSettings?.frameRate ?? 30}
              />
            ) : null}
          </Player.Root>
        </div>

        {hideDownloadUI ? null : (
          <div className="-mx-3 mt-3 flex flex-col gap-3 px-9 lg:-mx-4 lg:mt-4 lg:gap-4 lg:px-12">
            <div className="flex flex-col gap-1">
              <Text
                as="h2"
                variant="studio/title/md"
                className="text-primary text-start text-[20px]/[20px]"
              >
                Downloads
              </Text>
              <Text as="p" variant="studio/body/md" className="text-gray-60">
                {exportSettingsSummary}
              </Text>
            </div>
            {exportPollShowsFailure ? (
              <Text
                as="p"
                variant="studio/body/sm"
                className="text-red max-w-full"
              >
                Export did not finish for this job, so downloads are not
                available.
              </Text>
            ) : null}
            <div className="divide-gray-30 dark:divide-gray-75 divide-y">
              {bodyDownloadRows.map((row) => (
                <div
                  key={row.bodyIndex}
                  className="flex min-h-10 items-center gap-3 py-2.5"
                >
                  <span
                    className={`h-2 w-2 shrink-0 rounded-full ${row.dotClass}`}
                    aria-hidden
                  />
                  <Text
                    as="div"
                    variant="studio/body/sm"
                    className="text-primary min-w-0 flex-1 truncate"
                  >
                    <span className="tabular-nums">{row.label}</span>{' '}
                    {row.filename}
                  </Text>
                  <Button
                    type="button"
                    variant="secondary"
                    size="lg"
                    className={`h-9 w-9 shrink-0 p-0 ${OUTLINE_WHITE_BUTTON}`}
                    disabled={!isRowDownloadEnabled(row.bodyIndex)}
                    aria-label={`Download ${row.filename}`}
                    onClick={() =>
                      void handleDownload({ bodyIndex: row.bodyIndex })
                    }
                    icon={
                      exportPollShowsFailure || !exportControlsAvailable ? (
                        <DownloadIcon className="h-4 w-4 opacity-40" />
                      ) : (showRowZipWaiting && !useDirectExportDownloads) ||
                        downloadBusy === row.bodyIndex ? (
                        <Loading className="text-gray-60 dark:text-gray-45 h-4 w-4" />
                      ) : (
                        <DownloadIcon className="h-4 w-4" />
                      )
                    }
                  />
                </div>
              ))}
              {cameraFilename ? (
                <div className="flex min-h-10 items-center gap-3 py-2.5">
                  <span
                    className="bg-gray-45 h-2 w-2 shrink-0 rounded-full"
                    aria-hidden
                  />
                  <Text
                    as="div"
                    variant="studio/body/sm"
                    className="text-primary min-w-0 flex-1 truncate"
                  >
                    <span className="tabular-nums">Camera</span>{' '}
                    {cameraFilename}
                  </Text>
                  <Button
                    type="button"
                    variant="secondary"
                    size="lg"
                    className={`h-9 w-9 shrink-0 p-0 ${OUTLINE_WHITE_BUTTON}`}
                    disabled={!isCameraDownloadEnabled}
                    aria-label={`Download ${cameraFilename}`}
                    onClick={() => void handleDownload({ camera: true })}
                    icon={
                      exportPollShowsFailure || !exportControlsAvailable ? (
                        <DownloadIcon className="h-4 w-4 opacity-40" />
                      ) : downloadBusy === 'camera' ? (
                        <Loading className="text-gray-60 dark:text-gray-45 h-4 w-4" />
                      ) : (
                        <DownloadIcon className="h-4 w-4" />
                      )
                    }
                  />
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
